import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from '@cimpress/react-components';
import { PERSPECTIVE_WARP, RECTANGLE_WARP, SMOOTH_WARP } from 'src/models/documentWarps';
import { downloadImage } from 'src/util/upload';
import { useLatest } from 'src/hooks/useLatest';
import SourcePointCanvas from './sourcePointCanvas';
import { MapPoint, Point } from 'src/types/transforms';

const PERCENTAGE = 0.7;
const MODAL_PADDING = 40;
const OFFSET = 50;
const DEFAULT_LENGTH = 500;
const TRANSIENT_SCENE = 'https://scenes.documents.cimpress.io/v3/transient?data=';

type SourcePointProps = {
    id: string;
    points: MapPoint[];
    warpType: string;
    onApply: (data: { id: string; points: MapPoint[]; skipDelay: boolean }) => void;
    surfaceData: any;
    previewUrl?: string;
};

type Img = {
    src: string;
    scaledWidth: number;
    scaledHeight: number;
    width: number;
    height: number;
};

const SourcePoint: React.FC<SourcePointProps> = (props) => {
    const { id, warpType, onApply, surfaceData, previewUrl } = props;

    const [showSourcePointEditor, setShowSourcePointEditor] = useState(false);
    const [points, setPoints] = useState<MapPoint[]>(props.points);
    const [img, setImg] = useState<Img>({
        src: '',
        scaledWidth: DEFAULT_LENGTH,
        scaledHeight: DEFAULT_LENGTH,
        width: DEFAULT_LENGTH,
        height: DEFAULT_LENGTH,
    });
    const [snapCoordinates, setSnapCoordinates] = useState<{ x: number[]; y: number[] }>({ x: [], y: [] });

    // Lets the mount-once resize listener below always read the current `surfaceData`/`img`
    // instead of whichever render it was created on (see useLatest's doc comment).
    const latestSurfaceData = useLatest(surfaceData);
    const latestImg = useLatest(img);

    const resizeImage = (image: { src: string; width: number; height: number }) => {
        const surfaceData = latestSurfaceData.current;
        // calculate max image size could be based on window size
        const maxWidth = window.innerWidth * PERCENTAGE - OFFSET * 2 - MODAL_PADDING;
        const maxHeight = window.innerHeight * PERCENTAGE - OFFSET * 2 - MODAL_PADDING;

        // determine how much the image need to be scaled
        let scale = 1;
        if (image.width > maxWidth) {
            scale = maxWidth / image.width;
        }
        if (image.height * scale > maxHeight) {
            scale = maxHeight / image.height;
        }

        const scaledWidth = image.width * scale;
        const scaledHeight = image.height * scale;

        // snap points to document border
        const newSnapCoordinates = {
            x: [OFFSET, scaledWidth + OFFSET],
            y: [OFFSET, scaledHeight + OFFSET],
        };

        if (surfaceData) {
            const trim = surfaceData.trimArea ?? { x: 0, y: 0, width: surfaceData.width, height: surfaceData.height };

            // Compute the trim values
            const left = (trim.x / surfaceData.width) * scaledWidth + OFFSET;
            const top = (trim.y / surfaceData.height) * scaledHeight + OFFSET;
            const right = (1.0 - (surfaceData.width - trim.x + trim.width) / surfaceData.width) * scaledWidth + OFFSET;
            const bottom =
                (1.0 - (surfaceData.height - trim.y + trim.height) / surfaceData.height) * scaledHeight + OFFSET;

            // snap points to document trim
            newSnapCoordinates.x = newSnapCoordinates.x.concat([left, right]);
            newSnapCoordinates.y = newSnapCoordinates.y.concat([top, bottom]);

            // Compute fold line values
            if (surfaceData.foldLines) {
                surfaceData.foldLines.forEach((fold: any) => {
                    // horizontal fold
                    if (fold.startY === fold.endY) {
                        newSnapCoordinates.y.push((fold.startY / surfaceData.height) * scaledHeight + OFFSET);
                    }
                    // vertical fold
                    if (fold.startX === fold.endX) {
                        newSnapCoordinates.x.push((fold.startX / surfaceData.width) * scaledWidth + OFFSET);
                    }
                });
            }
        }

        setImg({ src: image.src, width: image.width, height: image.height, scaledWidth, scaledHeight });
        setSnapCoordinates(newSnapCoordinates);
    };

    const calculateDimensionAndSnapCoordinates = (url: string): void => {
        let ratio = 1.3;
        let page = 1;

        if (surfaceData && surfaceData.width > 0 && surfaceData.height > 0) {
            ratio = surfaceData.width / surfaceData.height;
            page = surfaceData.index;
        }

        // remove scene from document preview
        const urlSegments = url.split('&').filter((segment) => segment.substring(0, 5) !== 'scene');
        const width = Math.floor(DEFAULT_LENGTH * ratio);
        if (isNaN(width)) {
            return;
        }
        urlSegments.push(
            `scene=${TRANSIENT_SCENE}${encodeURIComponent(JSON.stringify({ width, height: DEFAULT_LENGTH, page }))}`,
        );
        urlSegments.push('bgColor=ffffff'); // adds white background to preview image
        const resolvedPreviewUrl = urlSegments.join('&');

        downloadImage(resolvedPreviewUrl).then((image) => {
            resizeImage(image);
        });
    };

    // Mirrors the original componentDidMount + componentDidUpdate pair: runs on mount (effects
    // run after the first render too) and again whenever previewUrl changes, exactly matching
    // the combined original behavior.
    useEffect(() => {
        if (previewUrl) {
            calculateDimensionAndSnapCoordinates(previewUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewUrl]);

    // Mount/unmount-only resize listener -- `resizeImage` reads `surfaceData` via `latestSurfaceData`
    // and `img` via `latestImg` rather than closing over either directly, so this never needs to be
    // torn down and re-registered just to pick up a fresh value.
    useEffect(() => {
        const resizeEditor = () => {
            resizeImage(latestImg.current);
        };

        window.addEventListener('resize', resizeEditor);
        return () => {
            window.removeEventListener('resize', resizeEditor);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update-only (never on mount, since state is already seeded from props.points): resets
    // points state whenever the points prop changes by length or by any sourceX/sourceY value.
    const isFirstPointsEffect = useRef(true);
    const prevPointsRef = useRef(props.points);
    useEffect(() => {
        const newPoints = props.points;

        if (isFirstPointsEffect.current) {
            isFirstPointsEffect.current = false;
        } else {
            const prevPoints = prevPointsRef.current;

            let pointsChanged = false;
            if (prevPoints.length === newPoints.length) {
                for (let i = 0; i < newPoints.length; i++) {
                    if (
                        prevPoints[i].sourceX !== newPoints[i].sourceX ||
                        prevPoints[i].sourceY !== newPoints[i].sourceY
                    ) {
                        pointsChanged = true;
                        break;
                    }
                }
            } else {
                pointsChanged = true;
            }

            if (pointsChanged) {
                setPoints(newPoints);
            }
        }

        prevPointsRef.current = newPoints;
    }, [props.points]);

    const closeEditor = () => {
        setShowSourcePointEditor(false);
    };

    const openEditor = () => {
        setShowSourcePointEditor(true);
    };

    const updateSourcePoints = () => {
        // close modal
        closeEditor();

        onApply({ id, points, skipDelay: true });
    };

    const onDragEnd = (newPoints: Point[]) => {
        const updatedPoints = [];

        // calculate new source points for each point
        for (let i = 0; i < newPoints.length; i++) {
            const ratioX = (newPoints[i].x - OFFSET) / img.scaledWidth;
            const ratioY = (newPoints[i].y - OFFSET) / img.scaledHeight;

            updatedPoints.push({ x: props.points[i].x, y: props.points[i].y, sourceX: ratioX, sourceY: ratioY });
        }
        setPoints(updatedPoints);
    };

    const acceptableWarps = [RECTANGLE_WARP, PERSPECTIVE_WARP, SMOOTH_WARP];
    if (!acceptableWarps.includes(warpType)) {
        return null;
    }

    return (
        <div>
            <Button style={{ marginBottom: '30px' }} onClick={openEditor}>
                Source Point Editor
            </Button>
            <Modal
                show={showSourcePointEditor}
                footer={
                    <>
                        <Button onClick={closeEditor}>Cancel</Button>
                        <Button onClick={updateSourcePoints}>Apply</Button>
                    </>
                }
                style={{ width: `${img.scaledWidth + OFFSET * 2 + MODAL_PADDING}px` }}>
                <SourcePointCanvas
                    id={id}
                    points={points}
                    warpType={warpType}
                    onDragEnd={onDragEnd}
                    offset={OFFSET}
                    snapCoordinates={snapCoordinates}
                    img={img}
                />
            </Modal>
        </div>
    );
};

export default React.memo(SourcePoint);
